// Right-slide PDF viewer for the matching card's "eye" action.
//
// Deliberately NON-blocking: no full-screen backdrop, so the operator can read
// the scanned document on the right while amending the extracted fields in the
// card on the left.
//
// The files endpoint is auth-protected, and a plain <object data=…> request
// can't carry the JWT — so we fetch the PDF with the token and render it from a
// blob URL.

import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export function PdfViewerPanel({ open, target, onClose, rightOffset = 0 }: {
  open: boolean;
  target: { fileUri?: string; scan?: string } | null;
  onClose: () => void;
  rightOffset?: number;  // px to shift left of, e.g., the ReceiptDrawer so they sit side-by-side
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  // Esc closes the panel (only while it's open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !target?.fileUri) { setBlobUrl(null); setState('idle'); return; }
    let cancelled = false;
    let url: string | null = null;
    setState('loading'); setBlobUrl(null);
    const token = window.localStorage.getItem('token');
    fetch(`${API_BASE}${target.fileUri}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
      .then(blob => { if (cancelled) return; url = URL.createObjectURL(blob); setBlobUrl(url); setState('ready'); })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, target?.fileUri]);

  return (
    <div
      className="fixed top-0 h-full z-[111] bg-white border-l border-trim flex flex-col transition-transform duration-300"
      style={{
        width: 676,
        right: rightOffset,
        transform: open ? 'translateX(0)' : `translateX(calc(100% + ${rightOffset + 8}px))`,
        boxShadow: open ? '-4px 0 24px rgba(4,50,67,.12)' : 'none',
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-trim flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
          </svg>
          <span className="text-sm font-semibold text-ink truncate">Scanned document</span>
          {target?.scan && <span className="text-xs text-muted truncate">· {target.scan}</span>}
        </div>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded-md text-muted hover:bg-wash hover:text-ink transition-colors">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 bg-[#F5F7FA]">
        {state === 'ready' && blobUrl ? (
          <object data={blobUrl} type="application/pdf" className="w-full h-full">
            <div className="flex flex-col items-center justify-center h-full text-center px-6 text-muted text-sm gap-1">
              <span className="font-medium text-ink">PDF preview unavailable</span>
              <a href={blobUrl} target="_blank" rel="noreferrer" className="text-teal-600 underline text-xs mt-1">Open in new tab</a>
            </div>
          </object>
        ) : state === 'loading' ? (
          <div className="flex items-center justify-center h-full text-muted text-sm">Loading scan…</div>
        ) : state === 'error' ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6 text-muted text-sm gap-1">
            <span className="font-medium text-ink">No scanned document for this receipt</span>
            <span className="text-xs">{target?.scan}</span>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted text-sm">No document.</div>
        )}
      </div>
    </div>
  );
}
